import { effect, inject, Injectable, signal } from '@angular/core';
import { SignalrService } from 'src/app/core/services/signalr.service';
import {
  NotifyUserSignalrPayload,
  SignalrDocumentMergeCompletedData,
  SignalrPayloadEventId,
} from '../signalr_payloads';
import { filter, tap } from 'rxjs';
import { Logger } from 'src/app/core/services';

type GenerationNode = { modeleId: number; meetingId?: number | null; pointId?: number | null };
type GenerationTarget = { scope: 'meeting' | 'point'; id: number };

@Injectable({
  providedIn: 'root',
})
export class MeetingGenerationQueue {
  private readonly logger = inject(Logger);
  private readonly signalR = inject(SignalrService);

  private readonly _queue = signal<{ target: GenerationTarget; nodeId: number }[]>([]);
  public readonly queue = this._queue.asReadonly();

  public readonly generationIsOverFor = signal<number>(-1, { equal: () => false }); // Possibly, the same model has been generated twice in a row,
  public readonly queueWatchdog = effect(() => {
    const queue = this.queue();

    if (queue.length) {
      this.logger.log(queue, 'Generation Queue');
    }
  });

  constructor() {
    this.startSubscriptionToSocket();
  }

  private startSubscriptionToSocket(): void {
    this.signalR.notifyUser
      .pipe(
        tap(console.log),
        filter(
          (payload: NotifyUserSignalrPayload) =>
            payload.payloadEventId == SignalrPayloadEventId.DocumentMergeCompleted && payload.data.modeleId
        )
      )
      .subscribe({
        next: (payload: NotifyUserSignalrPayload) => {
          const { data }: { data: SignalrDocumentMergeCompletedData } = payload;

          if (!data.modeleId) return;

          if (!data.seanceId && !data.pointId) return;

          this.removeFromQueue({ modeleId: data.modeleId, meetingId: data.seanceId, pointId: data.pointId });
          this.generationIsOverFor.set(data.modeleId);
        },
      });
  }

  public addToQueue(node: GenerationNode): void {
    const nodeInqueue = this._queue().find(
      (n) =>
        n.nodeId === node.modeleId &&
        (n.target.scope === 'meeting' ? n.target.id === node?.meetingId : n.target.id === node?.pointId)
    );

    if (!node.meetingId && !node.pointId) return;

    if (!nodeInqueue) {
      const nodeToAdd: { target: GenerationTarget; nodeId: number } = {
        target: node.meetingId ? { scope: 'meeting', id: node.meetingId! } : { scope: 'point', id: node.pointId! },
        nodeId: node.modeleId,
      };
      this._queue.update((queue) => [...queue, nodeToAdd]);
    }
  }

  public removeFromQueue(node: GenerationNode): void {
    const index = this._queue().findIndex(
      (n) =>
        n.nodeId === node.modeleId &&
        (n.target.scope === 'meeting' ? n.target.id === node?.meetingId : n.target.id === node?.pointId)
    );

    if (index > -1) {
      this._queue.update((queue) => {
        const newQueue = [...queue];
        newQueue.splice(index, 1);
        return newQueue;
      });
    }
  }

  public has(node: GenerationNode): boolean {
    return !!this._queue().find(
      (n) =>
        n.nodeId === node.modeleId &&
        (n.target.scope === 'meeting' ? n.target.id === node?.meetingId : n.target.id === node?.pointId)
    );
  }
}
